import React from 'react';
import {
  Root,
  AvatarEnhancer,
  AgentName,
  AgencyName,
} from './styled';
import { Avatar } from 'components/avatar';
import { useLocale } from 'locale';
import { useDefaultAgentCoverImageUrl } from 'hooks/useDefaultAgentCoverImageUrl';
import { ImageOptions, useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { TopTenBadge, TopTenBadgeVariant } from '../top-ten-badge';
import {
  CardUpperSection,
  CardContentSection,
  TopTenBadgePosition,
  CardCover,
  CardContentDetails,
  AvatarPosition,
  ContactButton,
} from '../styled';
import { NonRegisteredAgentAvatar } from './NonRegisteredAgentAvatar';
import PhoneAnchor from '../phone-anchor/PhoneAnchor';
import VerifiedIcon from 'assets/svg/verified.svg';
import { noopKeypress } from '../utils';
import { PropertiesSummary } from '../properties-summary';
import { SpacingBlock } from 'components/spacing-block';


const defaultCoverImageOptions: ImageOptions = { width: 264, height: 92 };
const avatarImageOptions: ImageOptions = {
  cropFaces: {
    vPadding: 10,
    hPadding: 40,
  },
};
const SPLIT_SEPARATOR = ' ';


export interface AgentCardProps {
  id: string;
  isRegistered?: boolean;
  coverImage?: string;
  avatarImage?: string;
  agentName: string;
  agentFirstName?: string;
  agencyName: string;
  soldCount: number;
  exclusiveListingsCount: number;
  topTenBadgeVariant?: TopTenBadgeVariant;
  onContactClick: (id: string) => void;
  phone?: string;
  enablePhoneShowing?: boolean;
  verifiedIconColor?: string;
  withPropertyInfo?: boolean;
  isLeadingAgent?: boolean;
}

export const AgentCard: React.FC<AgentCardProps> = React.memo(props => {
  const {
    id,
    isRegistered = true,
    coverImage,
    avatarImage,
    agentName,
    agentFirstName,
    agencyName,
    topTenBadgeVariant,
    onContactClick,
    enablePhoneShowing,
    phone,
    verifiedIconColor,
    withPropertyInfo = false,
    soldCount,
    exclusiveListingsCount,
    isLeadingAgent = true,
  } = props;
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const defaultCoverImageUrl = useDefaultAgentCoverImageUrl(defaultCoverImageOptions);
  const coverImageUrl = coverImage ? constructImageURI(coverImage, defaultCoverImageOptions) : null;
  const avatarImageUrl = avatarImage ? constructImageURI(avatarImage, avatarImageOptions) : null;
  // because of some difference between prod and stage DB agentName can be null because of that used agentName || ''
  const firstName = agentFirstName || (agentName || '').split(SPLIT_SEPARATOR)[0];
  const contactLabel = t('madad.contact', { name: firstName });

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContactClick(id);
  };

  return (
    <Root isRegistered={isRegistered}>
      <CardUpperSection>
        <CardCover imgUrl={isRegistered ? (coverImageUrl || defaultCoverImageUrl) : null} />
        <AvatarPosition>
          {isRegistered ? (
            <AvatarEnhancer>
              <Avatar photoUrl={avatarImageUrl} fullName={agentName} />
            </AvatarEnhancer>
          ) : (
            <NonRegisteredAgentAvatar />
          )}
        </AvatarPosition>
        {topTenBadgeVariant ? (
          <TopTenBadgePosition>
            <TopTenBadge variant={topTenBadgeVariant} withShadow={isRegistered} />
          </TopTenBadgePosition>
        ) : null}
      </CardUpperSection>
      <CardContentSection>
        <CardContentDetails>
          <AgentName
            weight="bold"
            onKeyPress={noopKeypress}
            isRegistered={isRegistered}
            verifiedIconColor={verifiedIconColor}
          >
            <h3>{agentName}</h3>
            {isRegistered && isLeadingAgent ? <VerifiedIcon /> : null}
          </AgentName>
          <AgencyName isRegistered={isRegistered}>{agencyName}</AgencyName>
          {withPropertyInfo ? (
            <SpacingBlock mTop={1}>
              <PropertiesSummary
                soldCount={soldCount}
                exclusiveListingsCount={exclusiveListingsCount}
              />
            </SpacingBlock>
          ) : null}
        </CardContentDetails>
        {isRegistered ? (
          enablePhoneShowing ? (
            <PhoneAnchor dataAuto="contact-agent-button" phone={phone} label={contactLabel} agentId={id} />
          ) : (
            <ContactButton data-auto="contact-agent-button" onClick={handleContactClick}>{contactLabel}</ContactButton>
          )
        ) : null}
      </CardContentSection>
    </Root>
  );
});
